import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNotify, usePermissions, useTranslate, Title } from 'react-admin';
import LibraryItemCard from './LibraryItemCard';
import InsertFromLibraryDialog from './InsertFromLibraryDialog';
import OperatorSharingPanel from './OperatorSharingPanel';
import UpdateNotifications from './UpdateNotifications';
import {
    libraryService,
    LibraryItem,
    LibraryItemType,
} from '../../services/libraryService';

interface ContentLibraryProps {
    onInsertCourse?: (item: LibraryItem, mode: 'copy' | 'reference', catalogId: number) => void;
    onInsertLesson?: (item: LibraryItem, mode: 'copy' | 'reference', courseId: number) => void;
    catalogId?: number;
    courseId?: number;
    filterType?: LibraryItemType;
    embedded?: boolean;
}

const ContentLibrary: React.FC<ContentLibraryProps> = ({
    onInsertCourse,
    onInsertLesson,
    catalogId,
    courseId,
    filterType,
    embedded = false,
}) => {
    const [items, setItems] = useState<LibraryItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState<LibraryItemType | ''>('');
    const [insertDialogOpen, setInsertDialogOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<LibraryItem | null>(null);
    const [sharingPanelOpen, setSharingPanelOpen] = useState(false);
    const [sharingItem, setSharingItem] = useState<LibraryItem | null>(null);

    const notify = useNotify();
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const isOperator = permissions?.includes('ROLE_OPERATOR');

    const loadItems = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params: { type?: LibraryItemType; search?: string; operatorOnly?: boolean } = {};

            if (filterType) {
                params.type = filterType;
            } else if (typeFilter) {
                params.type = typeFilter;
            }

            if (searchQuery) {
                params.search = searchQuery;
            }

            if (activeTab === 1 && isOperator) {
                params.operatorOnly = true;
            }

            const data = await libraryService.getLibraryItems(params);
            setItems(data);
        } catch (err) {
            setError(translate('library.loadError'));
            console.error('Failed to load library items:', err);
        } finally {
            setLoading(false);
        }
    }, [filterType, typeFilter, searchQuery, activeTab, isOperator, translate]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const handleSearch = () => {
        loadItems();
    };

    const handleInsert = (item: LibraryItem) => {
        setSelectedItem(item);
        setInsertDialogOpen(true);
    };

    const handleInsertConfirm = async (mode: 'copy' | 'reference') => {
        if (!selectedItem) return;

        try {
            if (selectedItem.itemType === 'course' && onInsertCourse && catalogId) {
                onInsertCourse(selectedItem, mode, catalogId);
            } else if (selectedItem.itemType === 'lesson' && onInsertLesson && courseId) {
                onInsertLesson(selectedItem, mode, courseId);
            } else {
                // Direct API call if no callback provided
                if (selectedItem.itemType === 'course' && catalogId) {
                    const result = await libraryService.insertCourseFromLibrary(selectedItem.id, {
                        catalogId,
                        mode,
                    });
                    notify(translate('library.insertSuccess', { title: result.course.title }), { type: 'success' });
                } else if (selectedItem.itemType === 'lesson' && courseId) {
                    const result = await libraryService.insertLessonFromLibrary(selectedItem.id, {
                        courseId,
                        mode,
                    });
                    notify(translate('library.insertSuccess', { title: result.lesson.title }), { type: 'success' });
                }
            }
            setInsertDialogOpen(false);
            setSelectedItem(null);
            loadItems();
        } catch (err: any) {
            notify(err.response?.data?.error || translate('library.insertError'), { type: 'error' });
        }
    };

    const handleDelete = async (item: LibraryItem) => {
        if (!window.confirm(translate('library.confirmDelete', { title: item.title }))) {
            return;
        }

        try {
            await libraryService.deleteLibraryItem(item.id);
            notify(translate('library.deleteSuccess'), { type: 'success' });
            loadItems();
        } catch (err: any) {
            notify(err.response?.data?.error || translate('library.deleteError'), { type: 'error' });
        }
    };

    const handleShare = (item: LibraryItem) => {
        setSharingItem(item);
        setSharingPanelOpen(true);
    };

    const filteredItems = items.filter(item => {
        // Tab filtering
        if (activeTab === 0 && item.isOperatorContent) {
            // Company library tab - show own items only
            return false;
        }
        if (activeTab === 1 && !item.isOperatorContent) {
            // Operator library tab - show operator items only
            return false;
        }
        return true;
    });

    return (
        <Box>
            {!embedded && (
                <Title title={translate('library.title')} />
            )}

            {!embedded && (
                <Typography variant="h4" sx={{ mb: 3 }}>
                    {translate('library.title')}
                </Typography>
            )}

            {/* Update Notifications */}
            {!embedded && <UpdateNotifications />}

            {/* Tabs for Company/Operator Library */}
            {isOperator && !embedded && (
                <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    sx={{ mb: 3 }}
                >
                    <Tab label={translate('library.tabs.myLibrary')} />
                    <Tab label={translate('library.tabs.operatorLibrary')} />
                </Tabs>
            )}

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    placeholder={translate('library.searchPlaceholder')}
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 250 }}
                />

                {!filterType && (
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>{translate('library.filterType')}</InputLabel>
                        <Select
                            value={typeFilter}
                            label={translate('library.filterType')}
                            onChange={(e) => setTypeFilter(e.target.value as LibraryItemType | '')}
                        >
                            <MenuItem value="">{translate('library.allTypes')}</MenuItem>
                            <MenuItem value="course">{translate('library.types.course')}</MenuItem>
                            <MenuItem value="lesson">{translate('library.types.lesson')}</MenuItem>
                        </Select>
                    </FormControl>
                )}

                <Button
                    startIcon={<RefreshIcon />}
                    onClick={loadItems}
                    variant="outlined"
                    size="small"
                >
                    {translate('library.refresh')}
                </Button>
            </Box>

            {/* Error display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Loading state */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Items grid */}
            {!loading && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 3 }}>
                    {filteredItems.map((item) => (
                        <LibraryItemCard
                            key={item.id}
                            item={item}
                            onInsert={(catalogId || courseId) ? handleInsert : undefined}
                            onDelete={
                                (item.isOperatorContent && isOperator) ||
                                (!item.isOperatorContent)
                                    ? handleDelete
                                    : undefined
                            }
                            onShare={isOperator && item.isOperatorContent ? handleShare : undefined}
                            isOperator={isOperator}
                        />
                    ))}
                </Box>
            )}

            {/* Empty state */}
            {!loading && filteredItems.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Typography variant="h6" color="text.secondary">
                        {searchQuery
                            ? translate('library.noSearchResults')
                            : translate('library.noItems')}
                    </Typography>
                </Box>
            )}

            {/* Insert dialog */}
            {selectedItem && (
                <InsertFromLibraryDialog
                    open={insertDialogOpen}
                    onClose={() => {
                        setInsertDialogOpen(false);
                        setSelectedItem(null);
                    }}
                    item={selectedItem}
                    onConfirm={handleInsertConfirm}
                />
            )}

            {/* Operator Sharing Panel */}
            {sharingItem && (
                <OperatorSharingPanel
                    open={sharingPanelOpen}
                    onClose={() => {
                        setSharingPanelOpen(false);
                        setSharingItem(null);
                    }}
                    item={sharingItem}
                    onUpdated={loadItems}
                />
            )}
        </Box>
    );
};

export default ContentLibrary;
